import { useState } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import AlertDialog from 'components/AlertDialog'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import billBundleValidator from 'validators/billBundleValidator'
import { ReactComponent as ButtonLoading } from 'icons/loading2.svg'
import { Formik } from 'formik'
import * as FA from 'react-icons/fa'


import {
  Container,
  FormField,
  Row,
  Input,
  InfoText,
  MoneyTextMask,
  Button
} from './styles'

export default function BillBundle ({
  bill,
  data: bundle,
  professionals,
  enableEditting = true,
  hasEditPermission = true,
  hasDeletePermission = true,
  onEdit: handleEdit,
  onDelete: handleDelete
}) {
  const [editting, setEditting] = useState (false)
  const [showAlertDialog, setShowAlertDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [deleting, setDeleting] = useState (false)
  const token = AuthService.getToken ()
  const canEditAmount = AuthService.hasPermission ('bills.items_amount', 'edit')
  const canEditValue = AuthService.hasPermission ('bills.items_value', 'edit')
  const canEditComission = AuthService.hasPermission ('bills.items_comission', 'edit')

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const deleteBundle = async () => {
    try {
      setDeleting (true)
      
      await cutileiApi.delete (
        `/business_bills/${bill.id}/bundles/${bundle.id}`, requestConfig
      )

      setDeleting (false)
      handleDelete (bundle)
    } catch (error) {
      setDeleting (false)
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    }
  }

  const toggleEditting = () => setEditting (!editting)
  const toggleAlertDialog = () => setShowAlertDialog (!showAlertDialog)

  return editting ? (
    <Formik
      validationSchema={billBundleValidator}
      initialValues={{
        'bundle': bundle,
        'professional': bundle.professional ? {
          label: bundle.professional.nickname,
          value: bundle.professional.id
        } : null,
        'amount': bundle.amount,
        'custom_price': bundle.custom_price,
        'custom_comission_percentage': bundle.custom_comission_percentage * 100
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const bundleData = {
          professional_id: values.professional?.value || null,
          amount: values.amount,
          custom_price: values.custom_price,
          custom_comission_percentage: values.custom_comission_percentage / 100
        }

        try {
          const { data: editedBundle } = await cutileiApi.put (
            `/business_bills/${bill.id}/bundles/${bundle.id}`, bundleData, requestConfig
          )

          setSubmitting (false)
          setEditting (false)
          handleEdit (editedBundle)
        } catch (error) {
          setErrorMessage (getApiErrorMessage (error))
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur, 
        handleSubmit
      }) => (
        <Container editting={editting} enableEditting={enableEditting}>
          <FormField>
            <Input
              name='bundle'
              placeholder='Pacote...'
              value={values.bundle.name}
              style={{width: '100%'}}
              disabled
            />
          </FormField>
          <FormField>
            <Select
              name='professional'
              placeholder='Profissional...'
              value={values.professional}
              options={professionals.map (professional => ({
                label: professional.nickname, value: professional.id
              }))}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              error={errors.professional}
              touched={touched.professional}
              containerStyles={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <NumberInput
              id='amount'
              name='amount'
              value={values.amount}
              minValue={1}
              maxValue={99}
              disabled={!canEditAmount}
              onChange={setFieldValue}
              style={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <MoneyInput
              name='custom_price'
              placeholder='Preço'
              value={values.custom_price}
              disabled={!canEditValue}
              onValueChange={setFieldValue}
              onBlur={handleBlur ('custom_price')}
              style={{width: '100%'}}
            />
          </FormField>
          <FormField>
            <NumberInput
              id='custom_comission_percentage'
              name='custom_comission_percentage'
              value={values.custom_comission_percentage}
              minValue={0}
              maxValue={100}
              disabled={!canEditComission}
              onChange={setFieldValue}
              style={{width: '100%'}}
            />
          </FormField>
          <Row>
            <Button type='button' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaSave color='#35A7FF' size={16}/>
              )}
            </Button>
            <Button type='button' onClick={toggleEditting}>
              <FA.FaTimes color='#FF3939' size={18}/>
            </Button>
          </Row>
          {/* {errorMessage && (
            <ErrorContainer>
              <InfoText>{errorMessage}</InfoText>
            </ErrorContainer>
          )} */}
        </Container>
      )}
    </Formik>
  ) : (
    <Container editting={editting} enableEditting={enableEditting}>
      <InfoText>
        {bundle.name}
      </InfoText>
      <InfoText>
        {bundle.professional ? bundle.professional.nickname : '-'}
      </InfoText>
      <InfoText>
        {bundle.amount}
      </InfoText>
      <MoneyTextMask value={bundle.custom_price ?? 0} style={{justifySelf: 'center'}}/>
      <InfoText>
        {bundle.custom_comission_percentage * 100}%
      </InfoText>
      {enableEditting && (
        <Row style={{justifySelf: 'center'}}>
          {hasEditPermission && (
            <Button onClick={toggleEditting}>
              <FA.FaEdit color='#35A7FF' size={18}/>
            </Button>
          )}
          {hasDeletePermission && (
            <Button onClick={toggleAlertDialog}>
              {deleting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaTrash color='#FF3939' size={16}/>
              )}
            </Button>
          )}
        </Row>
      )}
      <AlertDialog
        visible={showAlertDialog}
        title='Atenção!'
        message='Deseja realmente remover este pacote da comanda?'
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteBundle}
        onClose={toggleAlertDialog}
        dangerous
      />
      <AlertDialog
        visible={errorMessage !== null}
        title='Erro'
        message={errorMessage}
        confirmText='Ok'
        onConfirm={() => setErrorMessage (null)}
        onClose={() => setErrorMessage (null)}
        containerStyle={{width: 480}}
      />
    </Container>
  )
}
